/**
 * Expected AI Parser Response Structure (from prompt):
// {
//   "invoiceTotalAfterTax": 600,
//   "tax": 0,  // Single tax value (or use tax_line_items for multi-tax)
//   "tax_line_items": [  // Optional: For invoices with multiple taxes (GST, PST, etc.)
//     {
//       "tax_type": "GST",
//       "amount": 50
//     },
//     {
//       "tax_type": "PST",
//       "amount": 70
//     }
//   ],
//   "discount": 164.14,
//   "discount_percentage": 0.2148011588,
//   "invoiceTotalBeforeTax": 764.14,
//   "line_items": [
//     {
//       "description": "trip",
//       "quantity": 1,
//       "unit_price": 112.5,
//       "extended_price": 112.5,
//       "extended_price_after_discount": 88.34,
//       "discount_amount": 24.16,
//       "category": "travel"
//     },
//     {
//       "description": "Assessment",
//       "quantity": 1,
//       "unit_price": 127.5,
//       "extended_price": 127.5,
//       "extended_price_after_discount": 99.74,
//       "discount_amount": 27.76,
//       "category": "labor"
//     },
//     {
//       "description": "Labor",
//       "quantity": 2,
//       "unit_price": 127.5,
//       "extended_price": 255,
//       "extended_price_after_discount": 199.48,
//       "discount_amount": 55.52,
//       "category": "labor"
//     },
//     {
//       "description": "Snaking fees",
//       "quantity": 1,
//       "unit_price": 250,
//       "extended_price": 250,
//       "extended_price_after_discount": 195.4,
//       "discount_amount": 54.6,
//       "category": "other"
//     },
//     {
//       "description": "Drain Openers",
//       "quantity": 1,
//       "unit_price": 19.14,
//       "extended_price": 19.14,
//       "extended_price_after_discount": 14.9,
//       "discount_amount": 4.24,
//       "category": "other"
//     }
//   ],
//   "scope": null,
//   "invoice_number": "6681",
//   "invoice_date": "05/19/2025",
//   "due_date": null, // only set when explicitly printed on the invoice
//   "vendor_name": "Foothills Maintenance Services LLC",
//   "customer_name": "All Systems Facility Services LLC"
// }
 */

import moment from 'moment-timezone';
import { getWorkOrderTimeZoneId, nullSafeGetOrElse } from './DataAccessUtils';
import { CURRENCIES } from './DataConstants';

// Constants
const CONSTANTS = {
	DEFAULT_PAYMENT_TERMS: 'Net 30',
	DEFAULT_STATUS: 'draft',
	DEFAULT_UNIT_TYPE: 'each',
	DEFAULT_NUM_TECH: 1,
	DEFAULT_INITIAL_TOTAL: '0.00',
	DAYS_TO_MILLISECONDS: 24 * 60 * 60 * 1000,
	SECONDS_PER_HOUR: 3600,
	DEFAULT_DUE_DAYS: 30,
	DEFAULT_DECIMALS: 2,
	EQUIPMENT_KEYWORDS: ['equipment', 'machine', 'unit', 'system', 'hvac', 'motor', 'compressor'],
};

// Get currency decimals from currency ID
export const getCurrencyDecimals = (currencyId: string | null | undefined): number => {
	if (!currencyId) return CONSTANTS.DEFAULT_DECIMALS;
	const currency = CURRENCIES[currencyId.toUpperCase()];
	return currency && currency.decimals !== undefined
		? currency.decimals
		: CONSTANTS.DEFAULT_DECIMALS;
};

// Helper Functions using existing project utilities
const parseNumber = (value: any, defaultValue = 0) => parseFloat(value) || defaultValue;

const parseAmount = (value: any, defaultValue = 0, decimals: number = 2): number =>
	Number(parseNumber(value, defaultValue).toFixed(decimals));

// Get final amount with discount fallback
const getFinalAmount = (item: any): number => {
	return item.extended_price_after_discount != null
		? parseNumber(item.extended_price_after_discount)
		: parseNumber(item.extended_price);
};

// Calculate total from line items with currency-specific decimals
const calculateTotal = (lineItems: any[], decimals: number = 2): string =>
	lineItems.reduce((sum, item) => sum + parseFloat(item.amount), 0).toFixed(decimals);

// Check if description indicates equipment
const isEquipmentDescription = (description: string): boolean => {
	if (!description) return false;
	return CONSTANTS.EQUIPMENT_KEYWORDS.some((keyword) =>
		description.toLowerCase().includes(keyword)
	);
};

// Generate temporary ID for materials
let tempIdCounter = -1;
const getNextTempId = () => tempIdCounter--;

// Adapter function to convert AI parser output to formData structure
// `anchor` covers the case where there is no work order to hang the invoice off — a
// project invoice. It only needs to carry a `location`, which is what the timezone used
// for parsing the printed dates is read from.
export const adaptParsedInvoiceToFormData = (parsedData: any, workOrder: any, anchor?: any) => {
	if (!parsedData) return null;
	if (!workOrder && !anchor) return null;

	// getWorkOrderTimeZoneId only reads `location.timeZoneId` (and subcontractor
	// fallbacks), so the anchor stands in for the work order unchanged.
	const timeZoneId = getWorkOrderTimeZoneId(workOrder || anchor);

	// Get currency decimals from parsed data currency
	const currencyId = parsedData.currency || 'USD';
	const decimals = getCurrencyDecimals(currencyId);

	// Format initial total with correct decimals
	const initialTotal = (0).toFixed(decimals);

	const totalTax = parseNumber(parsedData.tax) || parseNumber(parsedData.invoiceTax);

	const taxBreakdown = parsedData.tax_breakdown || {};

	const adaptedFormData: any = {
		id: null,
		status: CONSTANTS.DEFAULT_STATUS,
		scope: nullSafeGetOrElse('scope', parsedData, ''),
		scopeNonFormatted: nullSafeGetOrElse('scope', parsedData, ''),
		// Short AI-derived summary of the work invoiced. Only forms that render the Title
		// field seed anything from it (project invoices today) — everything else ignores it.
		title: nullSafeGetOrElse('title', parsedData, ''),
		...(workOrder ? { externalWorkOrderId: workOrder.id } : {}),
		buyerInvoiceTypeDetail: [],
		supplierInvoiceTypeDetail: [],
		buyerCustomFieldValues: [],
		supplierCustomFieldValues: [],
		paymentTerms: CONSTANTS.DEFAULT_PAYMENT_TERMS,
		dueDate: parsedData.due_date
			? moment.tz(parsedData.due_date, ['MM/DD/YYYY', 'YYYY-MM-DD'], timeZoneId)
			: new Date(Date.now() + CONSTANTS.DEFAULT_DUE_DAYS * CONSTANTS.DAYS_TO_MILLISECONDS),
		dateOfInvoice: parsedData.invoice_date
			? moment.tz(parsedData.invoice_date, ['MM/DD/YYYY', 'YYYY-MM-DD'], timeZoneId)
			: new Date(),
		invoiceNumber: nullSafeGetOrElse('invoice_number', parsedData, ''),
		invoiceNotes: [],
		invoiceTax: parseAmount(totalTax, 0, decimals),
		invoiceTotalBeforeTax: parseAmount(parsedData.invoiceTotalBeforeTax, 0, decimals),
		invoiceTotalAfterTax: parseAmount(parsedData.invoiceTotalAfterTax, 0, decimals),
		laborTotalBeforeTax: initialTotal,
		laborLineItems: [],
		partsTotalBeforeTax: initialTotal,
		equipmentTotalBeforeTax: initialTotal,
		materialTotalBeforeTax: initialTotal,
		materialLineItems: [],
		travelTotalBeforeTax: initialTotal,
		travelLineItems: [],
		freightTotalBeforeTax: initialTotal,
		freightLineItems: [],
		miscTotalBeforeTax: initialTotal,
		miscLineItems: [],
		...(workOrder ? { workOrderId: workOrder.id, workOrder } : {}),
		AIData: parsedData,
		...(taxBreakdown && { taxBreakdown }),
	};

	// Group items by category with proper error handling.
	// Parser category values (per prompt): "labor" | "part" | "equipment" | "travel" | "freight" | "other"
	const groupItemsByCategory = (items: any[]) => {
		const categories: Record<string, any[]> = {
			labor: [],
			part: [],
			equipment: [],
			travel: [],
			freight: [],
			misc: [], // Default for 'other' and unknown categories
		};

		items.forEach((item) => {
			if (!item.category) {
				console.warn('Item missing category, defaulting to misc:', item);
				categories.misc.push(item);
				return;
			}

			const category = item.category.toLowerCase();
			// Handle 'other' category mapping to 'misc'
			const normalizedCategory = category === 'other' ? 'misc' : category;

			if (categories.hasOwnProperty(normalizedCategory)) {
				categories[normalizedCategory].push(item);
			} else {
				console.warn(`Unknown category "${category}" for item, defaulting to misc:`, item);
				categories.misc.push(item);
			}
		});

		return categories;
	};

	// Create labor item
	const createLaborItem = (item: any) => {
		const originalAmount = parseNumber(item.extended_price);
		const finalAmount = getFinalAmount(item);
		const quantity = parseNumber(item.quantity, 1);
		const unitPrice = parseNumber(item.unit_price);
		// Use original amount for hours calculation to maintain accuracy
		const hours = unitPrice > 0 ? originalAmount / unitPrice : quantity;
		const seconds = Math.round(hours * CONSTANTS.SECONDS_PER_HOUR);

		return {
			roleType: nullSafeGetOrElse('description', item, 'Labor'),
			numberOfTech: CONSTANTS.DEFAULT_NUM_TECH,
			hourlyRate: parseAmount(item.unit_price, 0, decimals).toFixed(decimals),
			seconds,
			amount: parseAmount(finalAmount, 0, decimals).toFixed(decimals),
			...(item.discount_amount != null && {
				discount_amount: parseAmount(item.discount_amount, 0, decimals),
			}),
		};
	};

	const createMaterialItem = (item: any, categoryHint?: 'part' | 'equipment') => {
		const description = nullSafeGetOrElse('description', item, 'Material');
		const isEquipment =
			categoryHint === 'equipment' || (categoryHint == null && isEquipmentDescription(description));
		const finalAmount = getFinalAmount(item);

		return {
			description,
			partNumber: '',
			partId: isEquipment ? null : getNextTempId(),
			equipmentId: isEquipment ? getNextTempId() : null,
			unitType: CONSTANTS.DEFAULT_UNIT_TYPE,
			unitPrice: parseAmount(item.unit_price, 0, decimals).toFixed(decimals),
			quantity: parseAmount(item.quantity, 1, decimals).toFixed(decimals),
			amount: parseAmount(finalAmount, 0, decimals).toFixed(decimals),
			...(item.discount_amount != null && {
				discount_amount: parseAmount(item.discount_amount, 0, decimals),
			}),
		};
	};

	// Create simple line item for travel, freight, misc
	const createSimpleLineItem = (item: any) => ({
		description: nullSafeGetOrElse('description', item, ''),
		amount: parseAmount(getFinalAmount(item), 0, decimals).toFixed(decimals),
		...(item.discount_amount != null && {
			discount_amount: parseAmount(item.discount_amount, 0, decimals),
		}),
	});

	// Process line items by category
	const lineItems = parsedData.line_items || [];
	const itemsByCategory = groupItemsByCategory(lineItems);

	// Process all categories
	adaptedFormData.laborLineItems = itemsByCategory.labor.map(createLaborItem);

	// Material is further split into parts & equipment; use explicit category when available
	const partMaterialItems = itemsByCategory.part.map((item: any) =>
		createMaterialItem(item, 'part')
	);
	const equipmentMaterialItems = itemsByCategory.equipment.map((item: any) =>
		createMaterialItem(item, 'equipment')
	);
	adaptedFormData.materialLineItems = [...partMaterialItems, ...equipmentMaterialItems];

	adaptedFormData.travelLineItems = itemsByCategory.travel.map(createSimpleLineItem);
	adaptedFormData.freightLineItems = itemsByCategory.freight.map(createSimpleLineItem);
	adaptedFormData.miscLineItems = itemsByCategory.misc.map(createSimpleLineItem);

	// Calculate all totals with currency-specific decimals
	adaptedFormData.laborTotalBeforeTax = calculateTotal(adaptedFormData.laborLineItems, decimals);
	adaptedFormData.travelTotalBeforeTax = calculateTotal(adaptedFormData.travelLineItems, decimals);
	adaptedFormData.freightTotalBeforeTax = calculateTotal(
		adaptedFormData.freightLineItems,
		decimals
	);
	adaptedFormData.miscTotalBeforeTax = calculateTotal(adaptedFormData.miscLineItems, decimals);

	// Calculate material totals (separate parts and equipment) - optimize filtering
	const partsItems: any[] = [];
	const equipmentItems: any[] = [];
	adaptedFormData.materialLineItems.forEach((item: any) => {
		if (item.partId) partsItems.push(item);
		if (item.equipmentId) equipmentItems.push(item);
	});

	adaptedFormData.partsTotalBeforeTax = calculateTotal(partsItems, decimals);
	adaptedFormData.equipmentTotalBeforeTax = calculateTotal(equipmentItems, decimals);
	adaptedFormData.materialTotalBeforeTax = calculateTotal(
		adaptedFormData.materialLineItems,
		decimals
	);

	return adaptedFormData;
};
